import React, { useCallback, useEffect, useState } from 'react';
import { trackSearch, trackArticleClick } from '../utils/analytics';
import { resolveApiBaseUrl } from '../utils/apiBase';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import ArticleTeaser from '../components/ArticleTeaser';
import { PageError, PageEmpty, PageLoading } from '../components/PageStatus';
import styles from './FeedPage.module.css';

const FeedPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [semanticSearch, setSemanticSearch] = useState(false);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500);

  const apiBaseUrl = resolveApiBaseUrl();

  useDocumentTitle('Feed');

  useEffect(() => {
    if (debouncedSearchTerm && articles.length >= 0) {
      trackSearch(debouncedSearchTerm, articles.length, 'feed');
    }
  }, [debouncedSearchTerm, articles.length]);

  const fetchArticles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let url = `${apiBaseUrl}/feed?page=${page}&size=20&sortBy=publishedAt&sortDir=desc`;
      if (debouncedSearchTerm.trim()) {
        url += `&search=${encodeURIComponent(debouncedSearchTerm.trim())}`;
        if (semanticSearch) {
          url += '&semantic=true';
        }
      }

      const response = await fetch(url, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (page === 0) {
        setArticles(data.content);
      } else {
        setArticles((prev) => [...prev, ...data.content]);
      }

      setHasMore(!data.last);
    } catch (err) {
      console.error('Error fetching articles:', err);
      setError('Failed to load articles. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, page, debouncedSearchTerm, semanticSearch]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const loadMore = () => {
    if (!loading && hasMore) {
      setPage((prev) => prev + 1);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSemanticSearch(false);
    setPage(0);
  };

  const handleSemanticToggle = (e) => {
    setSemanticSearch(e.target.checked);
    setPage(0);
  };

  const handleArticleClick = (article) => {
    trackArticleClick(article.id, article.title, article.source);
  };

  const loadingLabel = debouncedSearchTerm
    ? `Searching for “${debouncedSearchTerm}”`
    : page > 0
      ? 'Loading more articles'
      : 'Loading articles';

  if (error && articles.length === 0) {
    return (
      <div className={`reader-page ${styles['feed-page']}`}>
        <div className="container">
          <PageError title="Couldn't load articles" message={error} onRetry={fetchArticles} />
        </div>
      </div>
    );
  }

  return (
    <div className={`reader-page ${styles['feed-page']}`}>
      <div className="container">
        <header className={`reader-header ${styles.feedHeader}`}>
          <div className="reader-header-main">
            <p className="reader-kicker">Harvested archive</p>
            <h1>Feed</h1>
            <p className="reader-lead">
              Search articles from independent sources. Enable semantic search to match by meaning,
              not just keywords.
            </p>

            <form onSubmit={handleSearch} className={styles.searchChrome}>
              <div className={styles.searchToolbar}>
                <span className={styles.searchRail}>Search</span>
                <div className={styles.searchRow}>
                  <div className={styles.searchFieldWrap}>
                    <i className={`bi bi-search ${styles.searchIcon}`} aria-hidden="true" />
                    <input
                      type="search"
                      className={styles.searchInput}
                      placeholder="Titles, sources, topics…"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      aria-label="Search articles"
                    />
                  </div>
                  <div className={styles.searchActions}>
                    {searchTerm && (
                      <button type="button" className={styles.btnGhost} onClick={handleClearSearch}>
                        Clear
                      </button>
                    )}
                    <button type="submit" className={styles.btnSearch}>
                      Search
                    </button>
                  </div>
                </div>
                {searchTerm.trim() && (
                  <label className={styles.semanticPill} htmlFor="semanticSearch">
                    <input
                      className={styles.semanticInput}
                      type="checkbox"
                      id="semanticSearch"
                      checked={semanticSearch}
                      onChange={handleSemanticToggle}
                    />
                    <span className={styles.semanticPillFace}>
                      Semantic{semanticSearch ? ' on' : ''}
                    </span>
                  </label>
                )}
              </div>
              {debouncedSearchTerm.trim() ? (
                <div className={styles.queryStrip} aria-live="polite">
                  <span className={styles.searchRail}>Feed query</span>
                  <span className={styles.queryText}>{debouncedSearchTerm.trim()}</span>
                  {semanticSearch ? <span className={styles.queryMode}>semantic</span> : null}
                </div>
              ) : null}
            </form>
          </div>

          <div
            className={`${styles.kittBar} ${loading ? styles.kittBarActive : ''}`}
            role="progressbar"
            aria-hidden={!loading}
            aria-label={loading ? loadingLabel : undefined}
          >
            <span className={styles.kittScan} aria-hidden="true" />
          </div>
        </header>

        <div className={styles['articles-grid']}>
          {loading && articles.length === 0 && (
            <PageLoading message={loadingLabel} className={styles.feedLoading} />
          )}
          {articles.map((article) => (
            <ArticleTeaser key={article.id} article={article} onArticleClick={handleArticleClick} />
          ))}
        </div>

        {!loading && articles.length === 0 && (
          <PageEmpty
            title="No articles found"
            message={
              debouncedSearchTerm
                ? `No results for "${debouncedSearchTerm}". Try semantic search or different keywords.`
                : 'No articles available yet.'
            }
          />
        )}

        {!loading && hasMore && articles.length > 0 && (
          <div className={styles['load-more-container']}>
            <button type="button" onClick={loadMore} className={styles.loadMoreBtn} disabled={loading}>
              Load more articles
            </button>
          </div>
        )}

        {!loading && !hasMore && articles.length > 0 && (
          <div className={styles['end-of-feed']}>
            <p>You&apos;ve reached the end of the archive.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedPage;
